import { CommonModule, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { PreviewBlockHelper } from '../../../../../utils/preview-block-helper';
import { GenVideoPreviewVideo } from '../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { PreviewSitelink } from '../../../../../models/google-template/preview-interfaces/preview-sitelink.interface';
import { YoutubeHelper } from '../../../../../utils/youtube-helper';
import { BasePreviewShellComponent } from '../preview-common/base-preview-shell.component';
import { PreviewVariant } from '../preview-common/preview-variant.interface';
import { YoutubeVideoPreviewData } from '../../../../../models/google-template/preview-interfaces/youtube-video-preview-data.interface';
import { YOUTUBE_VIDEO_VARIANTS } from './variants/youtube-video-variants';

/**
 * YouTube Video Ad preview. The selector mechanics live in
 * {@link BasePreviewShellComponent}; this class supplies the variant registry
 * and how to build the flat {@link YoutubeVideoPreviewData} DTO from the blocks.
 *
 * To add a placement, register it in `YOUTUBE_VIDEO_VARIANTS`.
 */
@Component({
  selector: 'app-youtube-video-preview',
  imports: [CommonModule, NgComponentOutlet],
  templateUrl: '../preview-common/base-preview-shell.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: '../preview-common/base-preview-shell.component.scss'
})
export class YoutubeVideoPreviewComponent extends BasePreviewShellComponent<YoutubeVideoPreviewData> {
  protected readonly registry: PreviewVariant[] = YOUTUBE_VIDEO_VARIANTS;

  protected buildPreviewData(): YoutubeVideoPreviewData {
    return {
      headline: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'youtube-video-headline'), 'YoutubeVideoHeadline').trim(),
      video: this.toVideo(PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'youtube-video-youtube-url'), 'YoutubeVideoUrl')),
      url: this.toAbsoluteUrl(PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'youtube-video-url'), 'Link')),
      ctaText: PreviewBlockHelper.getVar(PreviewBlockHelper.findBlock(this.blocks, 'youtube-video-cta-text'), 'YoutubeVideoCtaText').trim(),
      sitelinks: this.buildSitelinks(),
    };
  }

  /** Every sitelink block with text, in editor order. */
  private buildSitelinks(): PreviewSitelink[] {
    return this.blocks
      .filter((b) => b.name === 'youtube-video-sitelink')
      .map((b) => ({
        text: PreviewBlockHelper.getVar(b, 'Text'),
        firstDescriptionLine: PreviewBlockHelper.getVar(b, 'FirstDescriptionLine'),
        secondDescriptionLine: PreviewBlockHelper.getVar(b, 'SecondDescriptionLine'),
        link: PreviewBlockHelper.getVar(b, 'SitelinkLink'),
      }))
      .filter((s) => s.text.trim().length > 0);
  }

  /** Pairs the link with its thumbnail, derived from the video id; null while the field is empty. */
  private toVideo(rawUrl: string): GenVideoPreviewVideo | null {
    const url = rawUrl.trim();
    if (!url) {
      return null;
    }
    const videoId = YoutubeHelper.extractVideoId(url);
    return {
      url: this.toAbsoluteUrl(url),
      thumbnailUrl: videoId ? YoutubeHelper.thumbnailUrl(videoId) : '',
      videoId: videoId ?? '',
    };
  }

  /** Prefix a scheme so an `[href]` does not resolve against the editor's origin. */
  private toAbsoluteUrl(rawUrl: string): string {
    const url = rawUrl.trim();
    if (!url) {
      return '';
    }
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }
}
